import { useState } from "react";
import axios from "axios";
import { AnimatePresence, motion } from "framer-motion";
import { Check, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Reveal } from "./Reveal";
import { COLORS, FIO_OPTIONS, MEMBERSHIP_FEE, TRIMS } from "@/constants/options";
import { startCheckout } from "@/lib/pay";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SuccessPanel = ({ member }) => {
    const [paying, setPaying] = useState(false);
    const payByCard = async () => {
        setPaying(true);
        try {
            await startCheckout("membership_fee", member.id);
        } catch {
            toast.error("Could not start card checkout — please try again.");
            setPaying(false);
        }
    };
    return (
        <motion.div
            key="success"
            data-testid="join-success-panel"
            initial={{ opacity: 0, y: 32 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
            className="border border-[#2563EB]/40 bg-black/60 p-8 backdrop-blur-xl sm:p-10"
        >
            <ShieldCheck className="h-10 w-10 text-[#2563EB]" aria-hidden="true" />
            <h3 className="mt-6 font-display text-2xl font-bold uppercase tracking-tight sm:text-3xl">
                Allocation reserved, {member.name.split(" ")[0]}.
            </h3>
            <p className="mt-3 text-sm leading-relaxed text-zinc-400 sm:text-base">
                One last step — activate your membership. Your{" "}
                <span className="text-white">{member.trim}</span> in{" "}
                <span className="text-white">{member.color}</span> is held in the pool.
                {member.options?.length > 0 && (
                    <span data-testid="success-options" className="mt-2 block">
                        Factory options:{" "}
                        <span className="text-white">{member.options.join(", ")}</span>
                    </span>
                )}
            </p>
            <button
                data-testid="pay-card-button"
                onClick={payByCard}
                disabled={paying}
                className="mt-8 flex h-14 w-full items-center justify-center gap-3 bg-[#2563EB] text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
            >
                {paying ? "Redirecting to secure checkout..." : `Pay $${MEMBERSHIP_FEE} by card now`}
            </button>
            <p className="mt-8 flex items-center gap-2 border-t border-white/10 pt-6 text-xs uppercase tracking-[0.2em] text-zinc-500">
                <Check className="h-4 w-4 text-[#2563EB]" aria-hidden="true" />
                Allocation guaranteed within 90 days — or a full and complete refund
            </p>
        </motion.div>
    );
};

export const LeadForm = () => {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        trim: "",
        color: "",
        options: [],
    });
    const [submitting, setSubmitting] = useState(false);
    const [member, setMember] = useState(null);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const toggleOption = (option) => (checked) =>
        setForm((f) => ({
            ...f,
            options: checked
                ? [...f.options, option]
                : f.options.filter((o) => o !== option),
        }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.trim || !form.color) {
            toast.error("Please select your preferred trim and color");
            return;
        }
        setSubmitting(true);
        try {
            const { data } = await axios.post(`${API}/members`, form);
            if (window.rdt) window.rdt("track", "Lead");
            setMember(data);
        } catch (err) {
            toast.error(err.response?.data?.detail?.[0]?.msg || "Something went wrong. Try again.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <section id="join" data-testid="join-section" className="py-28 sm:py-36">
            <div className="mx-auto grid max-w-7xl grid-cols-1 gap-16 px-6 lg:grid-cols-2">
                <Reveal>
                    <p className="mb-4 flex items-center gap-3 text-xs font-bold uppercase tracking-[0.35em] text-[#2563EB]">
                        <span className="h-2 w-2 rotate-45 bg-[#2563EB]" aria-hidden="true" />
                        Membership
                    </p>
                    <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.02] tracking-tighter sm:text-5xl">
                        Reserve your 2026 RAV4 today.
                    </h2>
                    <p className="mt-6 max-w-md text-base leading-relaxed text-zinc-400 md:text-lg">
                        Tell us who you are and what you want to drive. We'll hold your allocation
                        and take you to secure card checkout the moment you submit.
                    </p>
                    <div className="mt-10 border-l-2 border-[#2563EB] pl-6">
                        <p className="font-display text-5xl font-extrabold tracking-tighter">
                            $399
                        </p>
                        <p className="mt-2 text-xs uppercase tracking-[0.2em] text-zinc-500">
                            One-time membership — allocation guaranteed within 90 days or a full
                            and complete refund
                        </p>
                    </div>
                    <div
                        data-testid="guarantee-seal"
                        className="mt-8 max-w-md border-2 border-[#2563EB] p-6"
                    >
                        <div className="flex items-center gap-3">
                            <ShieldCheck className="h-8 w-8 shrink-0 text-[#2563EB]" aria-hidden="true" />
                            <p className="font-display text-lg font-extrabold uppercase leading-tight tracking-tight text-white">
                                90 days or full refund
                            </p>
                        </div>
                        <div className="my-4 h-px bg-[#2563EB]/40" />
                        <p className="font-display text-lg font-extrabold uppercase leading-tight tracking-tight text-[#2563EB]">
                            Below sticker — our fee included
                        </p>
                    </div>
                </Reveal>

                <Reveal delay={0.15}>
                    <AnimatePresence mode="wait">
                        {member ? (
                            <SuccessPanel member={member} />
                        ) : (
                            <motion.form
                                key="form"
                                data-testid="join-form"
                                onSubmit={submit}
                                exit={{ opacity: 0, y: -24 }}
                                transition={{ duration: 0.4 }}
                                className="space-y-6 border border-white/10 bg-[#0a0a0a] p-8 sm:p-10"
                            >
                                <div className="space-y-2">
                                    <Label htmlFor="name">Full name</Label>
                                    <Input
                                        id="name"
                                        data-testid="join-form-name-input"
                                        required
                                        minLength={2}
                                        placeholder="Alex Carter"
                                        value={form.name}
                                        onChange={set("name")}
                                        className="h-12 rounded-none border-white/15 bg-black/40"
                                    />
                                </div>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label htmlFor="email">Email</Label>
                                        <Input
                                            id="email"
                                            data-testid="join-form-email-input"
                                            type="email"
                                            required
                                            placeholder="alex@email.com"
                                            value={form.email}
                                            onChange={set("email")}
                                            className="h-12 rounded-none border-white/15 bg-black/40"
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <Label htmlFor="phone">Phone</Label>
                                        <Input
                                            id="phone"
                                            data-testid="join-form-phone-input"
                                            type="tel"
                                            required
                                            minLength={7}
                                            placeholder="(555) 010-2026"
                                            value={form.phone}
                                            onChange={set("phone")}
                                            className="h-12 rounded-none border-white/15 bg-black/40"
                                        />
                                    </div>
                                </div>
                                <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                                    <div className="space-y-2">
                                        <Label>Preferred trim</Label>
                                        <Select
                                            value={form.trim}
                                            onValueChange={(v) => setForm((f) => ({ ...f, trim: v }))}
                                        >
                                            <SelectTrigger
                                                data-testid="join-form-trim-select"
                                                className="h-12 rounded-none border-white/15 bg-black/40"
                                            >
                                                <SelectValue placeholder="Select trim" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {TRIMS.map((t) => (
                                                    <SelectItem
                                                        key={t}
                                                        value={t}
                                                        data-testid={`trim-option-${t.toLowerCase().replace(/\s+/g, "-")}`}
                                                    >
                                                        {t}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div className="space-y-2">
                                        <Label>Preferred color</Label>
                                        <Select
                                            value={form.color}
                                            onValueChange={(v) => setForm((f) => ({ ...f, color: v }))}
                                        >
                                            <SelectTrigger
                                                data-testid="join-form-color-select"
                                                className="h-12 rounded-none border-white/15 bg-black/40"
                                            >
                                                <SelectValue placeholder="Select color" />
                                            </SelectTrigger>
                                            <SelectContent>
                                                {COLORS.map((c) => (
                                                    <SelectItem
                                                        key={c}
                                                        value={c}
                                                        data-testid={`color-option-${c.toLowerCase().replace(/\s+/g, "-")}`}
                                                    >
                                                        {c}
                                                    </SelectItem>
                                                ))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                </div>
                                <div className="space-y-3">
                                    <Label>Factory Installed Options (optional)</Label>
                                    <div
                                        className="grid grid-cols-1 gap-3 sm:grid-cols-2"
                                        data-testid="fio-options-group"
                                    >
                                        {FIO_OPTIONS.map((option) => (
                                            <label
                                                key={option}
                                                className="flex cursor-pointer items-center gap-3 border border-white/15 bg-black/40 px-4 py-3 transition-colors duration-300 hover:border-[#2563EB]"
                                            >
                                                <Checkbox
                                                    data-testid={`fio-option-${option.toLowerCase().replace(/\s+/g, "-")}`}
                                                    checked={form.options.includes(option)}
                                                    onCheckedChange={toggleOption(option)}
                                                />
                                                <span className="text-sm font-semibold text-zinc-200">
                                                    {option}
                                                </span>
                                            </label>
                                        ))}
                                    </div>
                                </div>
                                <Button
                                    data-testid="join-form-submit-button"
                                    type="submit"
                                    disabled={submitting}
                                    className="h-14 w-full rounded-none bg-[#2563EB] text-sm font-bold uppercase tracking-[0.25em] text-white transition-colors duration-300 hover:bg-white hover:text-black"
                                >
                                    {submitting ? "Reserving..." : "Reserve my allocation — $399"}
                                </Button>
                                <p className="text-center text-xs leading-relaxed text-zinc-500">
                                    You'll complete secure card payment on the next screen.
                                </p>
                            </motion.form>
                        )}
                    </AnimatePresence>
                </Reveal>
            </div>
        </section>
    );
};
